<template>
    <MainContent         :title="'Editar evento'">
        <div v-if="!evento">
            <!-- Pixel-mirror skeleton: two-column card grid matching FormEvento.vue:18-115 -->
            <div class="tw:grid tw:grid-cols-1 xl:tw:grid-cols-2 tw:gap-6 tw:max-w-7xl tw:mx-auto tw:py-2">
                <!-- Column 1: BasicInfo + Location + Scheduling + TextEditors + Ticketing -->
                <div class="tw:space-y-6">
                    <!-- BasicInfo: 5 rows including sports-only fields (manager.tipo==1) -->
                    <SkeletonCard >
                        <!-- Row 1: titulo col-span-2 + artista in 3-col grid -->
                        <div class="tw:grid tw:grid-cols-1 tw:md:grid-cols-3 tw:gap-4">
                            <div class="tw:md:col-span-2 tw:space-y-1.5">
                                <SkeletonItem height="tw:h-[38px]" customClass="tw:w-full " />
                            </div>
                            <div class="tw:space-y-1.5">
                                <SkeletonItem height="tw:h-[38px]" customClass="tw:w-full" />
                            </div>
                        </div>
                        <div class="tw:border-t tw:border-gray-100"></div>
                        <!-- Row 2: equipo_local, equipo_visitante, competicion in 3-col grid -->
                        <div class="tw:grid tw:grid-cols-1 tw:md:grid-cols-3 tw:gap-4">
                            <div class="tw:space-y-1.5">
                                <SkeletonItem height="tw:h-[38px]" customClass="tw:w-full" />
                            </div>
                            <div class="tw:space-y-1.5">
                                <SkeletonItem height="tw:h-[38px]" customClass="tw:w-full" />
                            </div>
                            <div class="tw:space-y-1.5">
                                <SkeletonItem height="tw:h-[38px]" customClass="tw:w-full" />
                            </div>
                        </div>
                        <!-- Row 3: jornada full width -->
                        <div class="tw:space-y-1.5">
                            <SkeletonItem height="tw:h-[38px]" customClass="tw:w-full" />
                        </div>
                        <div class="tw:border-t tw:border-gray-100"></div>
                        <!-- Row 4: estado + etiquetas in 2-col grid -->
                        <div class="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-4">
                            <div class="tw:space-y-1.5">
                                <SkeletonItem height="tw:h-[38px]" customClass="tw:w-full" />
                            </div>
                            <div class="tw:space-y-1.5">
                                <SkeletonItem height="tw:h-[38px]" customClass="tw:w-full" />
                            </div>
                        </div>
                    </SkeletonCard>

                    <!-- Location: 3 fields in grid-cols-2 + 1 toggle -->
                    <SkeletonCard>
                        <div class="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-4">
                            <div class="tw:space-y-1.5">
                                <SkeletonItem height="tw:h-[38px]" customClass="tw:w-full" />
                            </div>
                            <div class="tw:space-y-1.5">
                                <SkeletonItem height="tw:h-[38px]" customClass="tw:w-full" />
                            </div>
                            <div class="tw:space-y-1.5 tw:md:col-span-2">
                                <SkeletonItem height="tw:h-[38px]" customClass="tw:w-full" />
                            </div>
                        </div>
                        <div class="tw:border-t tw:border-gray-100"></div>
                        <div class="tw:flex tw:items-center tw:gap-2">
                            <SkeletonItem height="tw:h-5" customClass="tw:w-9 tw:rounded-full" />
                            <SkeletonItem height="tw:h-3" customClass="tw:w-48" />
                        </div>
                    </SkeletonCard>

                    <!-- Scheduling: 2 datetime inputs + dropzone + 2 inputs + 1 input -->
                    <SkeletonCard>
                        <!-- Row 1: 2 datetime inputs in grid-cols-2 (small label + input per column) -->
                        <div class="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-4">
                            <div class="tw:space-y-1.5">
                                <SkeletonItem height="tw:h-2.5" customClass="tw:w-32" />
                                <SkeletonItem height="tw:h-[38px]" customClass="tw:w-full" />
                            </div>
                            <div class="tw:space-y-1.5">
                                <SkeletonItem height="tw:h-2.5" customClass="tw:w-32" />
                                <SkeletonItem height="tw:h-[38px]" customClass="tw:w-full" />
                            </div>
                        </div>
                        <div class="tw:border-t tw:border-gray-100 tw:mb-[17px]"></div>
                        <!-- Row 2: image dropzone (h-48 to mirror dashed border + icon + 2 text lines) -->
                        <div>
                            <SkeletonItem height="tw:h-4" customClass="tw:w-1/2 tw:mb-2" />
                            <div class="tw:border-2 tw:border-dashed tw:border-gray-200 tw:rounded-lg tw:h-48 tw:flex tw:flex-col tw:items-center tw:justify-center tw:p-8">
                                <SkeletonItem height="tw:h-10 tw:w-10" customClass="tw:rounded-full tw:mb-3" />
                                <SkeletonItem height="tw:h-3" customClass="tw:w-48 tw:mb-1" />
                                <SkeletonItem height="tw:h-2.5" customClass="tw:w-32" />
                            </div>
                        </div>
                        <div class="tw:border-t tw:border-gray-100"></div>
                        <!-- Row 3: 2 inputs in grid-cols-2 -->
                        <div class="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-4">
                            <div class="tw:space-y-1.5">
                                <SkeletonItem height="tw:h-2.5" customClass="tw:w-24" />
                                <SkeletonItem height="tw:h-9" customClass="tw:w-full" />
                            </div>
                            <div class="tw:space-y-1.5">
                                <SkeletonItem height="tw:h-2.5" customClass="tw:w-28" />
                                <SkeletonItem height="tw:h-9" customClass="tw:w-full" />
                            </div>
                        </div>
                        <!-- Row 4: 1 input full width (youtube) -->
                        <div class="tw:space-y-1.5">
                            <SkeletonItem height="tw:h-2.5" customClass="tw:w-32" />
                            <SkeletonItem height="tw:h-9" customClass="tw:w-full" />
                        </div>
                    </SkeletonCard>
                </div>
            </div>
        </div>
        <FormEvento
            v-else
            :evento="evento"
            :loading="loading"
            :errorsPeticion="errorsPeticion"
            @terminado="editarEvento"
        />
        <CerrandoButacasModal
            :show="showCerrandoButacasModal"
            @confirmar="confirmarCierreButacas"
        />
    </MainContent>
</template>

<script setup>
    import { ref, onMounted } from 'vue';
    import store from '@/store';
    import router from '@/router';
    import FormEvento from '@/modules/admin/eventos/components/FormEvento.vue';
    import MainContent from '@/modules/admin/ui/MainContent.vue';
    import { SkeletonItem } from '@/modules/admin/ui/skeleton';
    import SkeletonCard from '@/modules/admin/eventos/components/SkeletonCard.vue';
    import CerrandoButacasModal from '@/modules/admin/eventos/modals/CerrandoButacasModal.vue';
    import { useToast } from '@/composables/common/useToast';

    const props = defineProps({
        evento_id: { type: [String, Number], default: null }
    });

    const evento = ref(null);
    const loading = ref(false);
    const errorsPeticion = ref({});
    const oldCerrarButacasAbonados = ref(null);
    const showCerrandoButacasModal = ref(false);
    const toast = useToast();

    onMounted(() => {
        getEvento();
    });

    function getEvento() {
        loading.value = true;

        const data = { evento_id: props.evento_id };

        store.dispatch(store.getters.getConfig?.getVuexAlias?.('back.events.detalle') || 'BACK_EVENTS_DETALLE', data).then(res => {
            evento.value = res.data.data;
            oldCerrarButacasAbonados.value = evento.value.cerrar_butacas_abonados;
        }).finally(() => {
            loading.value = false;
        });
    }

    function editarEvento(data) {
        loading.value = true;

        data.filters = {};

        // Extract IDs from objects if needed, otherwise keep as-is. BaseMultiselect
        // now emits integer IDs directly (post-fix to its handleSelect), so
        // this guard handles both: legacy hydrated objects and current numeric
        // IDs, instead of blowing up on `number.id`.
        if (data.equipo_local_id && typeof data.equipo_local_id === 'object') {
            data.equipo_local_id = data.equipo_local_id.id;
        }

        if (data.equipo_visitante_id && typeof data.equipo_visitante_id === 'object') {
            data.equipo_visitante_id = data.equipo_visitante_id.id;
        }

        if (data.division_id && typeof data.division_id === 'object') {
            data.division_id = data.division_id.id;
        }

        if (!data.cerrar_butacas_abonados) {
            data.cerrar_butacas_abonados_no_confirmados = false;
        }

        data.evento_id = data.id;

        if (data.place_obj) {
            // Only used as a fallback when place_id wasn't set by the form
            // (e.g. a fresh CREAR with no recinto selected). With the new
            // multiselect handler in EventoLocation.vue both keys stay in
            // sync, so we trust place_id (the user's actual selection).
            if (!data.place_id) {
                data.place_id = typeof data.place_obj === 'object' ? data.place_obj.id : data.place_obj;
            }
        }

        store.dispatch(store.getters.getConfig?.getVuexAlias?.('back.events.editar') || 'BACK_EVENTS_EDITAR', data).then(() => {
            toast.success('Evento editado correctamente');
            if (!data.place_id && data.entradas_compartidas) {
                // compartirEventos's rejection must NOT propagate to the
                // .catch() below — the edit itself succeeded, and any
                // second-step failure here is unrelated to the save.
                Promise.resolve()
                    .then(() => compartirEventos(data.eventos_compartidos))
                    .catch(() => {});
            }
            if (data.cerrar_butacas_abonados && !oldCerrarButacasAbonados.value) {
                showCerrandoButacasModal.value = true;
            } else {
                // router.push() returns a Promise; if it rejects, the
                // rejection would propagate up and hit the .catch()
                // below as if the edit itself had failed — which it
                // hasn't. Swallow the navigation rejection so the
                // success path stays clean.
                router.push({ name: 'back.events.index' }).catch(() => {});
            }
        }).catch(error => {
            errorsPeticion.value = error.response?.data?.errors || {};
            if (Object.keys(errorsPeticion.value).length === 0) {
                toast.error('Error al editar el evento. Inténtalo de nuevo.');
            }
        }).finally(() => {
            loading.value = false;
        });
    }

    function compartirEventos(eventos) {
        loading.value = true;

        const params = {
            event_id: parseInt(props.evento_id),
            ids_eventos_entradas_compartidas: (eventos || []).map(evento => evento.id)
        };

        store.dispatch(store.getters.getConfig?.getVuexAlias?.('back.events.compartir-entradas') || 'BACK_EVENTS_COMPARTIR_EVENTOS', params).then(() => {
            toast.success('Se están cargando los eventos compartidos');
        }).finally(() => {
            loading.value = false;
        });
    }

    function confirmarCierreButacas() {
        showCerrandoButacasModal.value = false;
        // Same defensive catch as in editarEvento()'s success branch:
        // navigation rejection must not produce an unhandled rejection
        // (and the .catch() of the save chain can never fire here
        // because this runs after the dispatch already settled).
        router.push({ name: 'back.events.index' }).catch(() => {});
    }
</script>
